import React, { useEffect, useMemo, useState } from "react";
import {
  MapContainer,
  Marker,
  Polygon,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useTranslation } from "react-i18next";
import { isoOrEmpty } from "../utils/dateText";
import { toLatLng, hasLatLng } from "../utils/geo";
import { usePageConfig } from "../hooks/usePageConfig";
import { buildMediaUrl } from "../utils/mediaUrl";

function formatPartName(name) {
  if (!name) return "";
  // Add space before uppercase letters that are not at the start
  const withSpaces = name.replace(/([a-z])([A-Z])/g, "$1 $2");
  // Capitalize first letter of each word
  return withSpaces.replace(/\b\w/g, (char) => char.toUpperCase());
}

const AutoFitMap = ({ locations }) => {
  const map = useMap();

  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations);
      map.fitBounds(bounds, { padding: [90, 90] });
    }
  }, [locations, map]);

  return null;
};

const haversineDistance = ([lat1, lon1], [lat2, lon2]) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const findMaxDistance = (coordinates) => {
  let maxDistance = 0;
  let maxPair = null;

  for (let i = 0; i < coordinates.length; i++) {
    for (let j = i + 1; j < coordinates.length; j++) {
      const dist = haversineDistance(coordinates[i], coordinates[j]);
      if (dist > maxDistance) {
        maxDistance = dist;
        maxPair = [coordinates[i], coordinates[j]];
      }
    }
  }

  return { maxDistance, maxPair };
};

const truncateCoord = (value) => Math.trunc(value * 10000) / 10000;

const groupByCoordinates = (items, showPolygon) => {
  const groups = new Map();

  items.forEach((item) => {
    const point = toLatLng(item.lat, item.longitude);
    if (!point) return;
    const lat = showPolygon ? point[0] : truncateCoord(point[0]);
    const lng = showPolygon ? point[1] : truncateCoord(point[1]);
    const key = `${lat},${lng}`;
    if (!groups.has(key)) {
      groups.set(key, { coordinates: [lat, lng], items: [] });
    }
    groups.get(key).items.push(item);
  });

  return Array.from(groups.values());
};

const ImageSlider = ({ slides, coordinates }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const { t } = useTranslation("translation", { keyPrefix: "details.parts" });
  const { clientConfig } = usePageConfig();

  const changeSlide = (dir) => {
    setDirection(dir);
    setCurrentIndex((prev) => (prev + dir + slides.length) % slides.length);
  };

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? 100 : -100, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir < 0 ? 100 : -100, opacity: 0 }),
  };

  return (
    <>
      <div className="relative w-full h-[140px] overflow-hidden rounded-md mb-2">
        {/* <AnimatePresence custom={direction} initial={false}> */}
        <div
          key={slides[currentIndex].id}
          className="rounded absolute w-full h-full object-cover"
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.3 }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          onDragEnd={(e, info) => {
            if (!info) return;
            const { offset, velocity } = info;

            const swipe = Math.abs(offset.x) * velocity.x;

            if (swipe < -1000 && currentIndex < slides.length - 1) {
              setDirection(1);
              setCurrentIndex((prev) => prev + 1);
            } else if (swipe > 1000 && currentIndex > 0) {
              setDirection(-1);
              setCurrentIndex((prev) => prev - 1);
            }
          }}
        >
          <img
            src={buildMediaUrl(slides[currentIndex].picture, {
              baseUrl: clientConfig.mediaBaseUrl,
              pathPrefix: clientConfig.mediaPathPrefix,
            })}
            alt={`img_${currentIndex}`}
            className="w-full h-full object-cover rounded"
          />
        </div>
        {/* </AnimatePresence> */}

        {slides.length > 1 && (
          <>
            <button
              className={`absolute left-1 font-bold ${
                currentIndex ? "visible" : "hidden"
              } top-1/2 bg-black/70  px-0 py-0 text-white rounded`}
              onClick={() => changeSlide(-1)}
            >
              <svg
                width={24}
                height={24}
                className={"text-white"}
                viewBox="0 0 512 512"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M320 160 L200 256 L320 352"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="36"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              className={`absolute right-1 font-bold ${
                currentIndex !== slides.length - 1 ? "visible" : "hidden"
              } top-1/2 bg-black/70  text-white py-0 px-0 rounded`}
              onClick={() => changeSlide(1)}
            >
              <svg
                viewBox="0 0 512 512"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                width={24}
                height={24}
                className={"text-white"}
              >
                <path
                  d="M192 160 L312 256 L192 352"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="36"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}
      </div>

      <div className="p-4">
        <h4 className="font-medium text-lg text-gray-800 mb-1">
          {formatPartName(slides[currentIndex]?.side)}
        </h4>
        <h6 className="flex text-xs text-gray-800 mb-0.5 p-0 m-0">
          {/* Guarded: new Date(undefined).toISOString() throws RangeError, and
              thrown from render it takes the popup — and the tree — down with
              it. A photograph with no timestamp is not a reason to lose the
              map. */}
          {isoOrEmpty(slides[currentIndex]?.created_at)}
        </h6>

        <h6 className="flex text-xs text-gray-800 p-0 m-0">
          {`${Math.abs(coordinates[0]).toFixed(4)}° ${
            coordinates[0] >= 0 ? "N" : "S"
          }, ${Math.abs(coordinates[1]).toFixed(4)}° ${
            coordinates[1] >= 0 ? "E" : "W"
          } (±3m GPS Accuracy)`}
        </h6>
        <h6>{slides[currentIndex]?.aicam_warnings}</h6>
      </div>
    </>
  );
};

const CustomMap = ({
  uploaded,
  setMaxDistance,
  showPolygon,
  setShowPolygon,
}) => {
  const { clientConfig } = usePageConfig();
  const [groupedMarkers, setGroupedMarkers] = useState([]);

  useEffect(() => {
    // Anything that is not a finite, in-range pair is dropped here rather
    // than reaching Leaflet, which throws on NaN during render.
    const validItems = (uploaded || []).filter(hasLatLng);

    const grouped = groupByCoordinates(validItems, showPolygon);
    setGroupedMarkers(grouped);

    if (grouped.length > 1 && showPolygon) {
      const { maxDistance } = findMaxDistance(
        grouped.map((g) => g.coordinates)
      );
      setMaxDistance(`Max Distance: ${maxDistance?.toFixed(2)} km`);
    } else {
      setMaxDistance(null);
    }
  }, [uploaded, showPolygon]);

  const center = useMemo(() => {
    // Belt and braces: the filter above is what keeps NaN out, but this runs
    // during render and a throw here is a blank page, not a missing pin.
    const points = groupedMarkers
      .map((g) => toLatLng(g.coordinates?.[0], g.coordinates?.[1]))
      .filter(Boolean);
    if (points.length === 0) return [20.5937, 78.9629];
    return L.latLngBounds(points).getCenter();
  }, [groupedMarkers]);

  const customIcon = new L.Icon({
    iconUrl:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAApCAYAAADAk4LOAAAFgUlEQVR4Aa1XA5BjWRTN2oW17d3YaZtr2962HUzbDNpjszW24mRt28p47v7zq/bXZtrp/lWnXr337j3nPCe85NcypgSFdugCpW5YoDAMRaIMqRi6aKq5E3YqDQO3qAwjVWrD8Ncq/RBpykd8oZUb/kaJutow8r1aP9II0WmLKLIsJyv1w/kqw9Ch2MYdB++12Onxee/QMwvf4/Dk/Lfp/i4nxTXtOoQ4pW5Aj7wpici1A9erdAN2OH64x8OSP9j3Ft3b7aWkTg/Fm91siTra0f9on5sQr9INejH6CUUUpavjFNq1B+Oadhxmnfa8RfEmN8VNAsQhPqF55xHkMzz3jSmChWU6f7/XZKNH+9+hBLOHYozuKQPxyMPUKkrX/K0uWnfFaJGS1QPRtZsOPtr3NsW0uyh6NNCOkU3Yz+bXbT3I8G3xE5EXLXtCXbbqwCO9zPQYPRTZ5vIDXD7U+w7rFDEoUUf7ibHIR4y6bLVPXrz8JVZEql13trxwue/uDivd3fkWRbS6/IA2bID4uk0UpF1N8qLlbBlXs4Ee7HLTfV1j54APvODnSfOWBqtKVvjgLKzF5YdEk5ewRkGlK0i33Eofffc7HT56jD7/6U+qH3Cx7SBLNntH5YIPvODnyfIXZYRVDPqgHtLs5ABHD3YzLuespb7t79FY34DjMwrVrcTuwlT55YMPvOBnRrJ4VXTdNnYug5ucHLBjEpt30701A3Ts+HEa73u6dT3FNWwflY86eMHPk+Yu+i6pzUpRrW7SNDg5JHR4KapmM5Wv2E8Tfcb1HoqqHMHU+uWDD7zg54mz5/2BSnizi9T1Dg4QQXLToGNCkb6tb1NU+QAlGr1++eADrzhn/u8Q2YZhQVlZ5+CAOtqfbhmaUCS1ezNFVm2imDbPmPng5wmz+gwh+oHDce0eUtQ6OGDIyR0uUhUsoO3vfDmmgOezH0mZN59x7MBi++WDL1g/eEiU3avlidO671bkLfwbw5XV2P8Pzo0ydy4t2/0eu33xYSOMOD8hTf4CrBtGMSoXfPLchX+J0ruSePw3LZeK0juPJbYzrhkH0io7B3k164hiGvawhOKMLkrQLyVpZg8rHFW7E2uHOL888IBPlNZ1FPzstSJM694fWr6RwpvcJK60+0HCILTBzZLFNdtAzJaohze60T8qBzyh5ZuOg5e7uwQppofEmf2++DYvmySqGBuKaicF1blQjhuHdvCIMvp8whTTfZzI7RldpwtSzL+F1+wkdZ2TBOW2gIF88PBTzD/gpeREAMEbxnJcaJHNHrpzji0gQCS6hdkEeYt9DF/2qPcEC8RM28Hwmr3sdNyht00byAut2k3gufWNtgtOEOFGUwcXWNDbdNbpgBGxEvKkOQsxivJx33iow0Vw5S6SVTrpVq11ysA2Rp7gTfPfktc6zhtXBBC+adRLshf6sG2RfHPZ5EAc4sVZ83yCN00Fk/4kggu40ZTvIEm5g24qtU4KjBrx/BTTH8ifVASAG7gKrnWxJDcU7x8X6Ecczhm3o6YicvsLXWfh3Ch1W0k8x0nXF+0fFxgt4phz8QvypiwCCFKMqXCnqXExjq10beH+UUA7+nG6mdG/Pu0f3LgFcGrl2s0kNNjpmoJ9o4B29CMO8dMT4Q5ox8uitF6fqsrJOr8qnwNbRzv6hSnG5wP+64C7h9lp30hKNtKdWjtdkbuPA19nJ7Tz3zR/ibgARbhb4AlhavcBebmTHcFl2fvYEnW0ox9xMxKBS8btJ+KiEbq9zA4RthQXDhPa0T9TEe69gWupwc6uBUphquXgf+/FrIjweHQS4/pduMe5ERUMHUd9xv8ZR98CxkS4F2n3EUrUZ10EYNw7BWm9x1GiPssi3GgiGRDKWRYZfXlON+dfNbM+GgIwYdwAAAAASUVORK5CYII=",
    iconSize: [25, 35],
    iconAnchor: [20, 40],
    popupAnchor: [-5, -30],
  });

  return (
    <div className="h-full w-full z-0 relative">
      {/* <div className="absolute end-1 top-1 z-50">
        
      </div> */}

      <MapContainer
        center={center}
        zoom={2}
        scrollWheelZoom={true}
        className="h-full w-full z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <AutoFitMap locations={groupedMarkers.map((g) => g.coordinates)} />

        {showPolygon ? (
          <Polygon
            positions={groupedMarkers.map((g) => g.coordinates)}
            color="green"
          />
        ) : (
          groupedMarkers.map(({ coordinates, items }, index) => (
            <Marker key={index} icon={customIcon} position={coordinates}>
              <Popup minWidth={200}>
                <ImageSlider slides={items} coordinates={coordinates} />
              </Popup>
            </Marker>
          ))
        )}
      </MapContainer>
    </div>
  );
};

export default CustomMap;
